import {useDebounce} from 'use-debounce';
import {Box, IconButton} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {RemoveIcon, AddIcon} from '@eksab/assets/icons';

import {usePlayersQuery, useSetQueryPlayerUIState} from './usePlayersQuery';
import {PlayerItem} from '../PlayerItem';
import {useLineupStore} from '../useLineupStore';
import {State} from '../useLineupStore';
import type {FiltersT} from '../../types';
import {useAddPlayer} from '../useAddPlayer';

export type PlayerWithAddState = State['players'][number][number] & {added: boolean; isSubstitute: boolean};

type PlayersListProps = {
  filters: FiltersT;
  competitionId: string;
};

export function PlayersList({filters, competitionId}: PlayersListProps) {
  const t = useTranslate();
  const position = useLineupStore((state) => state.indicatorPosition.position);
  const playersQuery = usePlayersQuery({
    ...filters,
    price_from: useDebounce(filters.price_from, 800)[0],
    price_to: useDebounce(filters.price_to, 800)[0],
    q: useDebounce(filters.q, 800)[0],
    competition: competitionId,
    position,
  });

  const setQueryPlayerUIState = useSetQueryPlayerUIState(competitionId);
  const addPlayer = useAddPlayer(competitionId);
  const removePlayer = useLineupStore((state) => state.removePlayer);
  const setPlayerStatsModal = useLineupStore((state) => state.setPlayerStatsModal);

  const onPlayerClick = (player: PlayerWithAddState) =>
    setPlayerStatsModal({
      context: 'team-player',
      action: player.added ? 'remove' : 'add',
      player,
      isSubstitute: player.isSubstitute,
    });

  const onPlayerActionClick = (player: PlayerWithAddState, _playerIndex: number) => {
    if (player.added) {
      removePlayer(player, player.isSubstitute);
      setQueryPlayerUIState(`${player.team.id}`, player.position, player.id, 'remove');
    } else {
      addPlayer(player);
      mixpanel?.track('Player Added');
      setQueryPlayerUIState(`${player.team.id}`, position, player.id, 'add');
    }
  };

  return (
    <Box overflowY="auto" minH="16" maxH="lg" display="flex" flexDir="column" gap="2.5">
      {playersQuery.isLoading
        ? t('app.state.loading')
        : playersQuery.isError
          ? t('app.errors.try-again')
          : playersQuery.isSuccess && playersQuery.data.length
            ? playersQuery.data.map((player, playerIndex) => (
                <PlayerItem
                  key={player.id}
                  player={player}
                  action={
                    <IconButton
                      aria-label={t(`fantasy.lineup.player.actions.${player.added ? 'remove' : 'add'}`)}
                      title={t(`fantasy.lineup.player.actions.${player.added ? 'remove' : 'add'}`)}
                      variant="unstyled"
                      boxSize="8"
                      minW="unset"
                      minH="unset"
                      icon={player.added ? <RemoveIcon /> : <AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayerActionClick(player, playerIndex);
                      }}
                    />
                  }
                  onPlayerClick={() => onPlayerClick(player)}
                />
              ))
            : t('fantasy.lineup.filters.no-players-found')}
    </Box>
  );
}
